/**
 * FOLLOWER BOOST — service configurator
 *
 *  - pack (variant) selection → price, summary, hidden inputs
 *  - dynamic target validation (UX only; the backend re-validates everything)
 *  - blocks submission when the target is invalid, otherwise lets Horizon's
 *    <product-form-component> add to cart and open the drawer
 *
 * No price is ever computed here: every displayed price is the variant price
 * rendered by Liquid and read back from the DOM.
 */

const HOSTS = {
  instagram: ['instagram.com', 'www.instagram.com', 'instagr.am'],
  tiktok: ['tiktok.com', 'www.tiktok.com', 'vm.tiktok.com', 'vt.tiktok.com'],
  youtube: ['youtube.com', 'www.youtube.com', 'youtu.be', 'm.youtube.com'],
  snapchat: ['snapchat.com', 'www.snapchat.com', 't.snapchat.com'],
  facebook: ['facebook.com', 'www.facebook.com', 'fb.com', 'fb.watch', 'm.facebook.com'],
  twitch: ['twitch.tv', 'www.twitch.tv', 'm.twitch.tv'],
  x: ['x.com', 'twitter.com', 'www.x.com', 'www.twitter.com'],
  spotify: ['open.spotify.com', 'spotify.com'],
  linkedin: ['linkedin.com', 'www.linkedin.com'],
  discord: ['discord.gg', 'discord.com'],
};

const t = (key, vars = {}) => {
  let s = (window.FbStrings && window.FbStrings[key]) || key;
  for (const [k, v] of Object.entries(vars)) s = s.replace(`{{ ${k} }}`, v);
  return s;
};

class FbConfigurator {
  /** @param {HTMLElement} el */
  constructor(el) {
    this.el = el;
    this.form = el.querySelector('form');
    this.packs = el.querySelectorAll('[data-fb-packs] input[type="radio"]');
    this.target = el.querySelector('[data-fb-target]');
    this.targetHidden = el.querySelector('[data-fb-target-hidden]');
    this.error = el.querySelector('[data-fb-error]');
    this.variantId = el.querySelector('[data-fb-variant-id]');
    this.serviceQty = el.querySelector('[data-fb-service-quantity]');
    this.qtyLabel = el.querySelector('[data-fb-quantity-label]');
    this.price = el.querySelector('[data-fb-price]');
    this.priceFor = el.querySelector('[data-fb-price-for]');
    this.sumQty = el.querySelector('[data-fb-sum-qty]');
    this.sumTarget = el.querySelector('[data-fb-sum-target]');
    this.sumPrice = el.querySelector('[data-fb-sum-price]');
    this.submit = el.querySelector('[data-fb-submit]');
    this.variantProp = el.querySelector('[data-fb-variant-prop]');
    this.chosen = el.querySelector('[data-fb-chosen]');
    this.packStep = el.querySelector('[data-fb-pack-step]');
    this.packEdit = el.querySelector('[data-fb-pack-edit]');
    this.flow = el.querySelector('[data-fb-flow]');
    this.confirmBox = el.querySelector('[data-fb-confirm-box]');
    this.confirmValue = el.querySelector('[data-fb-confirm-value]');
    this.confirmBtn = el.querySelector('[data-fb-confirm]');
    this.modifyBtn = el.querySelector('[data-fb-modify]');
    this.confirmState = el.querySelector('[data-fb-confirm-state]');
    this.confirmed = false;
    this.normalized = '';

    this.platform = el.dataset.platform || '';
    this.targetType = el.dataset.targetType || 'username';
    this.domains = (el.dataset.targetDomains || '').split(',').map((s) => s.trim()).filter(Boolean);
    this.pattern = el.dataset.targetPattern ? new RegExp(el.dataset.targetPattern, 'i') : null;
    this.priceForTemplate = this.priceFor ? this.priceFor.dataset.template || '' : '';
    this.moneyFormat = el.dataset.moneyFormat || '{{amount}} €';

    this.packs.forEach((r) => r.addEventListener('change', () => this.onPack(r)));
    if (this.target) {
      this.target.addEventListener('input', () => this.onTargetInput());
      this.target.addEventListener('blur', () => this.maybeAskConfirm());
      this.target.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.maybeAskConfirm(true);
        }
      });
    }
    this.confirmBtn?.addEventListener('click', () => this.confirm());
    this.modifyBtn?.addEventListener('click', () => this.modify());
    this.packEdit?.addEventListener('click', () => {
      this.packStep?.classList.remove('is-collapsed');
      this.setStep('pack', 'on');
      const checked = [...this.packs].find((r) => r.checked) || this.packs[0];
      checked?.focus();
    });
    // Capture phase so we run before Horizon's product-form handler.
    this.form?.addEventListener('submit', (e) => this.onSubmit(e), true);
    this.submit?.addEventListener(
      'click',
      (e) => {
        if (this.submit.getAttribute('aria-disabled') === 'true') {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (!this.validate(true)) this.target?.focus();
          else this.maybeAskConfirm(true);
        }
      },
      true
    );

    const checked = [...this.packs].find((r) => r.checked);
    if (checked) this.onPack(checked, true);
  }

  /* ---------- packs ---------- */
  onPack(radio, silent) {
    const qtyText = radio.dataset.title || '';
    const priceCents = parseInt(radio.dataset.price, 10);
    if (this.variantId) this.variantId.value = radio.value;
    if (this.variantProp) this.variantProp.value = radio.value;
    if (this.chosen) this.chosen.textContent = `${qtyText} · ${this.formatMoney(priceCents)}`;
    if (this.serviceQty) this.serviceQty.value = radio.dataset.quantity || qtyText;
    if (this.qtyLabel) this.qtyLabel.value = qtyText;
    if (this.sumQty) this.sumQty.textContent = qtyText;
    const money = this.formatMoney(priceCents);
    if (this.price) {
      this.price.textContent = money;
      if (!silent) {
        this.price.classList.remove('is-bump');
        void this.price.offsetWidth;
        this.price.classList.add('is-bump');
      }
    }
    if (this.sumPrice) this.sumPrice.textContent = money;
    if (this.priceFor && this.priceForTemplate) this.priceFor.textContent = this.priceForTemplate.replace('__Q__', qtyText);
    if (this.submit && radio.dataset.available === 'false') this.submit.disabled = true;
    else if (this.submit && this.el.dataset.available !== 'false') this.submit.disabled = false;
    if (!silent) {
      this.el.dispatchEvent(new CustomEvent('fb:pack', { detail: { variantId: radio.value } }));
      // Pack chosen: collapse the step and move the flow on.
      this.packStep?.classList.add('is-collapsed');
      this.setStep('pack', 'done');
      if (!this.confirmed) this.setStep('target', 'on');
      const url = new URL(window.location.href);
      url.searchParams.set('variant', radio.value);
      history.replaceState(null, '', url.toString());
    }
  }

  /* ---------- flow stepper ---------- */
  setStep(name, state) {
    if (!this.flow) return;
    const li = this.flow.querySelector(`[data-step="${name}"]`);
    if (!li) return;
    li.classList.remove('is-on', 'is-done');
    if (state === 'on') {
      this.flow.querySelectorAll('.is-on').forEach((x) => x.classList.remove('is-on'));
      li.classList.add('is-on');
    } else if (state === 'done') li.classList.add('is-done');
  }

  formatMoney(cents) {
    if (!Number.isFinite(cents)) return '';
    const amount = (cents / 100).toFixed(2);
    const fmt = this.moneyFormat;
    // Handles the common Shopify money formats without a full parser.
    if (fmt.includes('{{amount_with_comma_separator}}')) return fmt.replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
    if (fmt.includes('{{amount_no_decimals}}')) return fmt.replace('{{amount_no_decimals}}', String(Math.round(cents / 100)));
    if (fmt.includes('{{amount_no_decimals_with_comma_separator}}')) return fmt.replace('{{amount_no_decimals_with_comma_separator}}', String(Math.round(cents / 100)));
    return fmt.replace('{{amount}}', amount);
  }

  /* ---------- target ---------- */
  onTargetInput() {
    this.unconfirm();
    if (this.sumTarget) this.sumTarget.textContent = this.target.value.trim() || '—';
    if (this.targetHidden) this.targetHidden.value = this.target.value.trim();
    if (this.target.getAttribute('aria-invalid') === 'true') this.validate(false);
    clearTimeout(this.askTimer);
    this.askTimer = setTimeout(() => this.maybeAskConfirm(), 700);
  }

  /**
   * TargetInput normalisation (UX only; the backend re-normalises and re-validates):
   * trim, strip whitespace, detect @username vs URL, canonical https URL,
   * extract the handle from a profile URL for username targets.
   */
  normalize(raw) {
    let v = (raw || '').trim().replace(/\s+/g, '');
    if (!v) return '';
    if (this.targetType === 'username') {
      if (/^https?:\/\//i.test(v) || /^(www\.)?[a-z0-9.-]+\.[a-z]{2,}\//i.test(v)) {
        try {
          const url = new URL(/^https?:\/\//i.test(v) ? v : 'https://' + v);
          const seg = url.pathname.split('/').filter(Boolean);
          const handle = (seg.find((x) => x.startsWith('@')) || seg[0] || '').replace(/^@/, '');
          if (handle && !this.checkUrl(url.toString())) return '@' + handle;
        } catch {
          /* fall through */
        }
      }
      return '@' + v.replace(/^@+/, '');
    }
    if (this.targetType === 'other') return v;
    if (!/^https?:\/\//i.test(v)) v = 'https://' + v;
    try {
      const url = new URL(v);
      url.protocol = 'https:';
      url.hostname = url.hostname.toLowerCase();
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'igsh', 'igshid', 'si', 'feature'].forEach((k) => url.searchParams.delete(k));
      return url.toString().replace(/\?$/, '');
    } catch {
      return v;
    }
  }

  /**
   * Future backend resolver (POST /api/targets/resolve, see docs/API-CONTRACTS.md).
   * Not active: only runs if a resolver function is installed on window.FbTargetResolver
   * and returns { ok, normalized, messageKey }. The theme never scrapes social networks.
   */
  async resolveRemote(normalized) {
    const resolver = window.FbTargetResolver;
    if (typeof resolver !== 'function') return { ok: true, normalized };
    try {
      const res = await resolver({ platform: this.platform, targetType: this.targetType, target: normalized, locale: document.documentElement.lang });
      return res && typeof res === 'object' ? { ok: res.ok !== false, normalized: res.normalized || normalized, messageKey: res.messageKey } : { ok: true, normalized };
    } catch {
      return { ok: true, normalized };
    }
  }

  async maybeAskConfirm(focusBtn) {
    if (!this.target || !this.confirmBox) return;
    if (!this.target.value.trim()) return;
    if (!this.validate(true)) return;
    const resolved = await this.resolveRemote(this.normalize(this.target.value));
    if (!resolved.ok) {
      this.target.setAttribute('aria-invalid', 'true');
      if (this.error) {
        this.error.textContent = t(resolved.messageKey || 'fb.configurator.error_url', { platform: this.platformName() });
        this.error.hidden = false;
      }
      return;
    }
    this.normalized = resolved.normalized;
    if (this.confirmValue) this.confirmValue.textContent = this.normalized;
    if (this.sumTarget) this.sumTarget.textContent = this.normalized;
    this.confirmBox.hidden = false;
    this.setStep('target', 'done');
    if (!this.confirmed) this.setStep('check', 'on');
    if (focusBtn) this.confirmBtn?.focus();
  }

  confirm() {
    if (!this.validate(true)) return;
    this.normalized = this.normalize(this.target.value);
    this.confirmed = true;
    this.target.value = this.normalized;
    if (this.targetHidden) this.targetHidden.value = this.normalized;
    if (this.confirmValue) this.confirmValue.textContent = this.normalized;
    if (this.sumTarget) this.sumTarget.textContent = this.normalized;
    this.confirmBox?.classList.add('is-confirmed');
    if (this.confirmState) this.confirmState.textContent = t('fb.configurator.confirmed');
    this.setStep('check', 'done');
    this.setStep('price', 'done');
    this.setStep('order', 'on');
    if (this.submit && !this.submit.disabled) {
      this.submit.setAttribute('aria-disabled', 'false');
      this.submit.classList.add('is-ready');
    }
    this.submit?.focus({ preventScroll: true });
    this.submit?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  unconfirm() {
    if (!this.confirmed && this.confirmBox?.hidden !== false) return;
    this.confirmed = false;
    this.confirmBox?.classList.remove('is-confirmed');
    if (this.confirmState) this.confirmState.textContent = t('fb.configurator.confirm_hint');
    this.submit?.setAttribute('aria-disabled', 'true');
    this.submit?.classList.remove('is-ready');
    this.setStep('check', 'off');
    this.setStep('price', 'off');
    this.setStep('order', 'off');
    this.setStep('target', 'on');
  }

  modify() {
    this.unconfirm();
    this.confirmBox.hidden = true;
    this.target.focus();
    this.target.select();
  }

  /**
   * UX validation only. Returns an error key or null.
   */
  check(value) {
    const v = value.trim();
    if (!v) return 'fb.configurator.error_required';
    if (this.pattern) return this.pattern.test(v) ? null : this.targetType === 'username' ? 'fb.configurator.error_username' : 'fb.configurator.error_url';

    if (this.targetType === 'other') return v.length >= 2 ? null : 'fb.configurator.error_required';
    const looksLikeUrl = /^https?:\/\//i.test(v) || /^(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)+(\/|$)/i.test(v);
    const withScheme = /^https?:\/\//i.test(v) ? v : 'https://' + v;
    if (this.targetType === 'username') {
      // Accept @handle, handle, or a profile URL on the platform (with or without https://).
      if (looksLikeUrl && v.includes('/')) return this.checkUrl(withScheme);
      return /^@?[a-z0-9._-]{2,64}$/i.test(v) ? null : 'fb.configurator.error_username';
    }
    // URL targets: "instagram.com/p/…" is completed to https:// instead of being rejected.
    if (!looksLikeUrl) return 'fb.configurator.error_url';
    return this.checkUrl(withScheme);
  }

  checkUrl(v) {
    let url;
    try {
      url = new URL(v);
    } catch {
      return 'fb.configurator.error_url';
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return 'fb.configurator.error_url';
    const hosts = this.domains.length ? this.domains : HOSTS[this.platform] || [];
    if (hosts.length && !hosts.some((h) => url.hostname === h || url.hostname.endsWith('.' + h))) {
      return 'fb.configurator.error_url_platform';
    }
    return null;
  }

  validate(announce) {
    if (!this.target) return true;
    const key = this.check(this.target.value);
    if (key) {
      this.target.setAttribute('aria-invalid', 'true');
      this.target.classList.remove('is-valid');
      if (this.error) {
        this.error.textContent = t(key, { platform: this.platformName() });
        this.error.hidden = false;
      }
      return false;
    }
    this.target.removeAttribute('aria-invalid');
    this.target.classList.add('is-valid');
    if (this.error) this.error.hidden = true;
    return true;
  }

  platformName() {
    const link = this.el.querySelector('[data-fb-platform-name]');
    return link ? link.textContent.trim() : this.platform;
  }

  /* ---------- submit ---------- */
  onSubmit(e) {
    if (!this.validate(true)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.target.focus();
      return;
    }
    if (!this.confirmed) {
      // Step 4: the customer must confirm the understood target before ordering.
      e.preventDefault();
      e.stopImmediatePropagation();
      this.maybeAskConfirm(true);
      return;
    }
    this.target.value = this.normalized || this.target.value.trim();
    if (this.targetHidden) this.targetHidden.value = this.target.value;
    this.submit?.classList.add('is-loading');
    setTimeout(() => this.submit?.classList.remove('is-loading'), 2500);
  }
}

const boot = () => {
  document.querySelectorAll('[data-fb-config]').forEach((el) => {
    if (!el.__fb) el.__fb = new FbConfigurator(el);
  });
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
document.addEventListener('shopify:section:load', boot);
